import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { fetchActivities } from '../api'
import type { DiscoveryFilters } from '../filters/filters'
import type { Activity, MapBounds } from '../types'

export type DiscoveryPhase = 'idle' | 'loading' | 'ready' | 'error'

export interface UseDiscoveryActivitiesArgs {
  filters: DiscoveryFilters
  search: string
  bounds: MapBounds | null
  enabled: boolean
}

export interface DiscoveryActivitiesState {
  items: Activity[]
  phase: DiscoveryPhase
  refreshing: boolean
  error: unknown
  refresh: () => void
  setItems: (updater: (items: Activity[]) => Activity[]) => void
}

/**
 * Explicit request lifecycle (D-042): the current view is not a cache. A
 * filter/search/bounds change aborts the previous request and either shows the
 * initial skeleton or a non-blocking refresh overlay when old cards are still
 * on screen. Staleness is derived from the loaded query key, so effects never
 * synchronously set state before fetching.
 */
export function useDiscoveryActivities({
  filters,
  search,
  bounds,
  enabled,
}: UseDiscoveryActivitiesArgs): DiscoveryActivitiesState {
  const [items, setItemsState] = useState<Activity[]>([])
  const [loadedKey, setLoadedKey] = useState<string | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [errorKey, setErrorKey] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  // Only an explicit refresh (pull or toolbar button) drives the pull
  // indicator. Filter/search/bounds changes still update in the background but
  // must not animate the list as if the user had pulled it down.
  const [manualRefreshing, setManualRefreshing] = useState(false)
  const manualRefreshingRef = useRef(false)

  const queryKey = useMemo(
    () => JSON.stringify({ filters, search, bounds }),
    [bounds, filters, search],
  )

  const setItems = useCallback((updater: (items: Activity[]) => Activity[]) => {
    setItemsState(updater)
  }, [])

  useEffect(() => {
    if (!enabled) return
    const controller = new AbortController()

    fetchActivities({ filters, search, bounds }, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return
        setItemsState(next)
        setLoadedKey(queryKey)
        setError(null)
        setErrorKey(null)
        if (manualRefreshingRef.current) {
          manualRefreshingRef.current = false
          setManualRefreshing(false)
        }
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause)
        setErrorKey(queryKey)
        if (manualRefreshingRef.current) {
          manualRefreshingRef.current = false
          setManualRefreshing(false)
        }
      })

    return () => controller.abort()
  }, [attempt, bounds, enabled, filters, queryKey, search])

  const refresh = useCallback(() => {
    manualRefreshingRef.current = true
    setManualRefreshing(true)
    setError(null)
    setErrorKey(null)
    setAttempt((value) => value + 1)
  }, [])

  const failed = errorKey === queryKey
  let phase: DiscoveryPhase = 'ready'
  if (!enabled) {
    phase = 'idle'
  } else if (loadedKey === null) {
    phase = failed ? 'error' : 'loading'
  }

  return {
    items,
    phase,
    refreshing: enabled && manualRefreshing,
    error: failed ? error : null,
    refresh,
    setItems,
  }
}
